import React from 'react';
import {Body, Headline} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import ContentLabPlanBundle from '@/types/models/pricing/contentLabPlanBundle';
import {SubscriptionPlan} from '@/types/models/pricing/subscriptionPlan';
import {PricingCtaButton} from '@/views/content-studio/pricing/components/pricing-bundle/Buttons';
import DurationToggle from '@/views/content-studio/pricing/components/pricing-bundle/DurationToggle';
import Features from '@/views/content-studio/pricing/components/pricing-bundle/Features';
import PlanSelector from '@/views/content-studio/pricing/components/pricing-bundle/PlanSelector';
import PriceFootnote from '@/views/content-studio/pricing/components/pricing-bundle/PriceFootnote';
import Price from '@/views/content-studio/pricing/components/pricing-bundle/PriceSection';
import Conditional from '@/components/conditional';
import usePricingPlanSelection from '@/hooks/use-pricing-plan-selection/usePricingPlanSelection';
import styles from './MultiPlansPricingBundle.module.scss';

export type MultiPlansPricingBundleProps = {
  plans: SubscriptionPlan[];
  bundle: ContentLabPlanBundle;
  name: string;
  description: string;
  isHighlighted: boolean;
  isActiveBundle: boolean;
  activePlan?: SubscriptionPlan;
  activeSubscriptionId?: string;
  sendUiInteractionEvent: (buttonName: string) => void;
  sendPurchaseProcessStartedEvent: (
    product_id: string,
    process_id?: string
  ) => void;
};

function MultiPlansPricingBundle(props: MultiPlansPricingBundleProps) {
  const {
    plans,
    bundle,
    name,
    description,
    isHighlighted,
    isActiveBundle,
    activePlan,
    activeSubscriptionId,
    sendUiInteractionEvent,
    sendPurchaseProcessStartedEvent,
  } = props;

  const {
    selectedSubscriptionPlan,
    selectedDuration,
    presentedPlanList,
    handleDurationToggled,
    handlePlanSelected,
    handleSubscribeButtonClick,
    getOriginalPrice,
  } = usePricingPlanSelection({
    plans,
    activeSubscriptionId,
    activePlan,
    shouldSkipUpgradeModal: true,
    sendUiInteractionEvent,
    sendPurchaseProcessStartedEvent,
  });

  const getCtaButtonText = () => {
    if (selectedSubscriptionPlan?.id === activePlan?.id) {
      return 'Current Plan';
    }
    if (isActiveBundle) {
      return 'Update Plan';
    }
    return 'Get Started';
  };

  return (
    selectedSubscriptionPlan && (
      <div className={styles.container}>
        <div
          className={`${styles.stickyBanner} ${
            isHighlighted ? styles.highlightedBundle : ''
          } ${isActiveBundle ? styles.activeBundle : ''}`}
        >
          <Conditional condition={isHighlighted}>
            <Headline size="sm" className={styles.highlightTitle}>
              {translate('MOST POPULAR')}
            </Headline>
          </Conditional>
        </div>
        <div
          className={`${styles.pricingBundle} ${
            isActiveBundle ? styles.activeBundle : ''
          } ${isHighlighted ? styles.highlightedBundle : ''}`}
        >
          <DurationToggle
            selectedDuration={selectedDuration}
            handleDurationChange={handleDurationToggled}
            isDisabled={
              activePlan !== undefined &&
              activePlan.bundle === ContentLabPlanBundle.BUSINESS
            }
          />
          <div className={`${styles.bundleHeader}`}>
            <Headline size="lg">{name}</Headline>
            <Body size="sm" className={styles.description}>
              {description}
            </Body>
          </div>
          <div>
            <Price
              bundle={bundle}
              currency={selectedSubscriptionPlan.currency}
              price={selectedSubscriptionPlan.price}
              originalPrice={getOriginalPrice()}
            />
            <PriceFootnote
              selectedDuration={selectedDuration}
              selectedSubscriptionPlan={selectedSubscriptionPlan}
              className={styles.priceFootnoteText}
            />
          </div>
          <PlanSelector
            presentedPlanList={presentedPlanList}
            selectedSubscriptionPlan={selectedSubscriptionPlan}
            handlePlanChange={handlePlanSelected}
          />
          <div className={styles.buttons}>
            <PricingCtaButton
              onClick={() => handleSubscribeButtonClick(getCtaButtonText())}
              isHighlighted={isHighlighted}
              isDisabled={selectedSubscriptionPlan.isDisabled}
              isCurrentPlan={selectedSubscriptionPlan.id === activePlan?.id}
              text={translate(getCtaButtonText())}
            />
          </div>
          <Features
            bundle={bundle}
            seatsCount={selectedSubscriptionPlan.editors}
            videosCount={selectedSubscriptionPlan.videos}
          />
        </div>
      </div>
    )
  );
}

export default MultiPlansPricingBundle;
